import { useState, useMemo, useCallback } from 'react';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    AlertTriangle
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { CheckboxBase } from '@/components/base/checkbox/checkbox';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useDocumentAttributes, useCreateDocumentAttribute, useUpdateDocumentAttribute, useDeleteDocumentAttributesBatch, useAttributeUsageDetail } from '@/hooks/api/use-document-attributes';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import { useAddAttributeToSchema, useRemoveAttributeFromSchema } from '@/hooks/api/use-schema-attributes';
import type { AttributeSchemaUsage } from '@/hooks/api/use-document-attributes';
import { AttributeDataType } from '@/types/api/document-schemas';
import type { DocumentAttributeResponse, DocumentAttributeCreate, SchemaAttributeCreate } from '@/types/api/document-schemas';
import { useTranslation } from 'react-i18next';
import { X } from '@untitledui/icons';
import { useToast } from '@/hooks/use-toast';
import type { SortDescriptor } from 'react-aria-components';

export const DocumentAttributesPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const toast = useToast();

    // Fetch all attributes
    const { data: attributes = [], isLoading: isLoadingAttributes, refetch } = useDocumentAttributes();

    const [searchTerm, setSearchTerm] = useState('');
    const [dataTypeFilter, setDataTypeFilter] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAttribute, setEditingAttribute] = useState<DocumentAttributeResponse | null>(null);
    const [enumValues, setEnumValues] = useState<string[]>([]);
    const [enumValueInput, setEnumValueInput] = useState('');
    const [metadataJson, setMetadataJson] = useState('{}');
    const [metadataError, setMetadataError] = useState('');
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: 'usage_count',
        direction: 'descending',
    });

    // Bulk delete state
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);
    const [attributesInUse, setAttributesInUse] = useState<AttributeSchemaUsage[]>([]);
    const [batchDeleteStep, setBatchDeleteStep] = useState<'confirm' | 'warning'>('confirm');

    // Usage detail state
    const [usageDetailAttributeId, setUsageDetailAttributeId] = useState<string | null>(null);
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
    const [isAddToSchemaModalOpen, setIsAddToSchemaModalOpen] = useState(false);
    const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
    const [junctionToRemove, setJunctionToRemove] = useState<{
        schemaId: string;
        junctionId: string;
        schemaName: string;
    } | null>(null);
    const [addToSchemaForm, setAddToSchemaForm] = useState<{
        schemaId: string;
        importance_level: number;
        is_required: boolean;
        display_order: number;
        custom_label: string;
        custom_help_text: string;
    }>({
        schemaId: '',
        importance_level: 2,
        is_required: false,
        display_order: 0,
        custom_label: '',
        custom_help_text: '',
    });

    // Mutations
    const createAttribute = useCreateDocumentAttribute();
    const updateAttribute = useUpdateDocumentAttribute();
    const batchDelete = useDeleteDocumentAttributesBatch();
    const addToSchema = useAddAttributeToSchema();
    const removeFromSchema = useRemoveAttributeFromSchema();

    // Usage detail data
    const { data: usageDetail, isLoading: isLoadingUsage } = useAttributeUsageDetail(usageDetailAttributeId);
    const { data: allSchemas = [] } = useDocumentSchemas(
        { limit: 500 },
        { enabled: isAddToSchemaModalOpen }
    );

    const isLoading = isLoadingAttributes;

    const [formData, setFormData] = useState<DocumentAttributeCreate>({
        attribute_key: '',
        label: '',
        description: '',
        data_type: AttributeDataType.STRING,
        validation_rules: {},
        enum_values: [],
        input_type: '',
        placeholder: '',
        help_text: '',
        category: '',
        display_order: 0,
        is_global: true, // Default to global for new attributes
        attribute_metadata: {},
    });

    const dataTypeOptions = [
        { id: '', label: t('attributes.dataTypePlaceholder') },
        { id: AttributeDataType.STRING, label: t('attributes.dataTypes.string') },
        { id: AttributeDataType.NUMBER, label: t('attributes.dataTypes.number') },
        { id: AttributeDataType.DATE, label: t('attributes.dataTypes.date') },
        { id: AttributeDataType.DATETIME, label: t('attributes.dataTypes.datetime') },
        { id: AttributeDataType.BOOLEAN, label: t('attributes.dataTypes.boolean') },
        { id: AttributeDataType.ARRAY, label: t('attributes.dataTypes.array') },
        { id: AttributeDataType.OBJECT, label: t('attributes.dataTypes.object') },
        { id: AttributeDataType.EMAIL, label: t('attributes.dataTypes.email') },
        { id: AttributeDataType.URL, label: t('attributes.dataTypes.url') },
        { id: AttributeDataType.PHONE, label: t('attributes.dataTypes.phone') },
    ];

    const importanceLevelOptions = [
        { id: '1', label: t('schemaAttributes.modal.fields.importanceLevels.error', { defaultValue: 'Error if missing' }) },
        { id: '2', label: t('schemaAttributes.modal.fields.importanceLevels.warning', { defaultValue: 'Warning if missing' }) },
        { id: '3', label: t('schemaAttributes.modal.fields.importanceLevels.optional', { defaultValue: 'Optional' }) },
    ];

    const getImportanceBadge = (level: number) => {
        switch (level) {
            case 1: return <Badge color="error" size="sm">{t('schemaAttributes.importance.error', { defaultValue: 'Error' })}</Badge>;
            case 2: return <Badge color="warning" size="sm">{t('schemaAttributes.importance.warning', { defaultValue: 'Warning' })}</Badge>;
            case 3: return <Badge color="gray" size="sm">{t('schemaAttributes.importance.optional', { defaultValue: 'Optional' })}</Badge>;
            default: return <Badge color="gray" size="sm">{level}</Badge>;
        }
    };

    // Apply client-side filters
    const filteredAttributes = useMemo(() => {
        let filtered = [...attributes];

        if (dataTypeFilter) {
            filtered = filtered.filter((a) => a.data_type === dataTypeFilter);
        }

        if (categoryFilter) {
            filtered = filtered.filter((a) => a.category === categoryFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (a) =>
                    a.attribute_key.toLowerCase().includes(term) ||
                    a.label.toLowerCase().includes(term) ||
                    a.description?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [attributes, searchTerm, dataTypeFilter, categoryFilter]);

    // Sort filtered results
    const sortedAttributes = useMemo(() => {
        if (!sortDescriptor.column) return filteredAttributes;

        return [...filteredAttributes].sort((a, b) => {
            const column = sortDescriptor.column as keyof DocumentAttributeResponse;
            let aValue = a[column];
            let bValue = b[column];

            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return sortDescriptor.direction === 'descending' ? -comparison : comparison;
        });
    }, [filteredAttributes, sortDescriptor]);

    const categories = Array.from(new Set(attributes.map((a) => a.category).filter(Boolean)));

    // Group usage by document type for display
    const groupedUsage = useMemo(() => {
        if (!usageDetail) return [];
        const groups = new Map<string, typeof usageDetail.schemas>();
        usageDetail.schemas.forEach(s => {
            const key = s.document_type_name || s.document_type || t('attributes.usageModal.unknownType', { defaultValue: 'Unknown' });
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(s);
        });
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [usageDetail, t]);

    // Available schemas for add-to-schema (exclude already-linked)
    const availableSchemas = useMemo(() => {
        if (!usageDetail || !allSchemas.length) return [];
        const usedSchemaIds = new Set(usageDetail.schemas.map(s => s.document_schema_id));
        return allSchemas
            .filter(s => !usedSchemaIds.has(s.document_schema_id) && s.is_active)
            .map(s => ({
                id: s.document_schema_id,
                label: `${s.schema_name} (${s.document_type || ''})`,
            }));
    }, [allSchemas, usageDetail]);

    const handleOpenModal = (attr?: DocumentAttributeResponse) => {
        if (attr) {
            setEditingAttribute(attr);
            setFormData({
                attribute_key: attr.attribute_key,
                label: attr.label,
                description: attr.description || '',
                data_type: attr.data_type,
                validation_rules: attr.validation_rules || {},
                enum_values: (attr.enum_values as string[]) || [],
                input_type: attr.input_type || '',
                placeholder: attr.placeholder || '',
                help_text: attr.help_text || '',
                category: attr.category || '',
                display_order: attr.display_order,
                is_global: attr.is_global,
                attribute_metadata: attr.attribute_metadata || {},
            });
            setEnumValues((attr.enum_values as string[]) || []);
            setMetadataJson(JSON.stringify(attr.attribute_metadata || {}, null, 2));
        } else {
            setEditingAttribute(null);
            setFormData({
                attribute_key: '',
                label: '',
                description: '',
                data_type: AttributeDataType.STRING,
                validation_rules: {},
                enum_values: [],
                input_type: '',
                placeholder: '',
                help_text: '',
                category: '',
                display_order: 0,
                is_global: true,
                attribute_metadata: {},
            });
            setEnumValues([]);
            setEnumValueInput('');
            setMetadataJson('{}');
            setMetadataError('');
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingAttribute(null);
        setEnumValues([]);
        setEnumValueInput('');
        setMetadataJson('{}');
        setMetadataError('');
    };

    const handleSave = async () => {
        // Validate metadata JSON if data_type is object
        if (formData.data_type === AttributeDataType.OBJECT) {
            try {
                const parsed = JSON.parse(metadataJson);
                formData.attribute_metadata = parsed;
                setMetadataError('');
            } catch (e) {
                setMetadataError(t('common.invalidJson'));
                return;
            }
        } else {
            formData.attribute_metadata = {};
        }

        // Set enum_values if data_type allows it
        if (formData.data_type === AttributeDataType.STRING && enumValues.length > 0) {
            formData.enum_values = enumValues;
        } else {
            formData.enum_values = undefined;
        }

        try {
            if (editingAttribute) {
                await updateAttribute.mutateAsync({
                    attributeId: editingAttribute.document_attribute_id,
                    data: {
                        label: formData.label,
                        description: formData.description || null,
                        validation_rules: formData.validation_rules || null,
                        enum_values: formData.enum_values || null,
                        input_type: formData.input_type || null,
                        placeholder: formData.placeholder || null,
                        help_text: formData.help_text || null,
                        category: formData.category || null,
                        display_order: formData.display_order,
                        is_global: formData.is_global,
                        attribute_metadata: formData.attribute_metadata || null,
                    },
                });
                toast.success(t('modal.saveSuccess') || 'Attributo aggiornato con successo');
            } else {
                await createAttribute.mutateAsync(formData);
                toast.success(t('modal.createSuccess') || 'Attributo creato con successo');
            }
            handleCloseModal();
        } catch (error) {
            toast.error(t('modal.error') || 'Errore durante il salvataggio');
        }
    };

    const handleDelete = async (id: string) => {
        // Use batch endpoint to check schema usage before deleting
        try {
            const result = await batchDelete.mutateAsync({
                attributeIds: [id],
                force: false,
            });

            // Store the single ID for the modal flow
            setSelectedIds(new Set([id]));

            if (result.attributes_in_use.length > 0) {
                setAttributesInUse(result.attributes_in_use);
                setBatchDeleteStep('warning');
            } else {
                setAttributesInUse([]);
                setBatchDeleteStep('confirm');
            }
            setIsBatchDeleteModalOpen(true);
        } catch {
            toast.error(t('modal.error') || 'Errore durante il controllo');
        }
    };

    const handleClearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const handleBatchDeleteStart = async () => {
        if (selectedIds.size === 0) return;

        // First call: check which attributes are in use (force=false)
        try {
            const result = await batchDelete.mutateAsync({
                attributeIds: Array.from(selectedIds),
                force: false,
            });

            if (result.attributes_in_use.length > 0) {
                // Some are in use — show warning step
                setAttributesInUse(result.attributes_in_use);
                setBatchDeleteStep('warning');
                setIsBatchDeleteModalOpen(true);
            } else {
                // None in use — show simple confirm
                setAttributesInUse([]);
                setBatchDeleteStep('confirm');
                setIsBatchDeleteModalOpen(true);
            }
        } catch {
            toast.error(t('attributes.batchDelete.error', { defaultValue: 'Errore durante il controllo degli attributi' }));
        }
    };

    const handleBatchDeleteConfirm = async () => {
        try {
            const result = await batchDelete.mutateAsync({
                attributeIds: Array.from(selectedIds),
                force: true,
            });

            handleClearSelection();
            setIsBatchDeleteModalOpen(false);
            setBatchDeleteStep('confirm');
            setAttributesInUse([]);

            toast.success(
                t('attributes.batchDelete.success', {
                    defaultValue: '{{count}} attributi eliminati',
                    count: result.deleted_count,
                })
            );
            refetch();
        } catch {
            toast.error(t('attributes.batchDelete.error', { defaultValue: 'Errore durante l\'eliminazione' }));
        }
    };

    const addEnumValue = () => {
        if (enumValueInput.trim()) {
            setEnumValues([...enumValues, enumValueInput.trim()]);
            setEnumValueInput('');
        }
    };

    const removeEnumValue = (index: number) => {
        setEnumValues(enumValues.filter((_, i) => i !== index));
    };

    const handleDataTypeChange = (dataType: AttributeDataType) => {
        setFormData({ ...formData, data_type: dataType });
        if (dataType !== AttributeDataType.STRING) {
            setEnumValues([]);
        }
        if (dataType !== AttributeDataType.OBJECT) {
            setMetadataJson('{}');
            setMetadataError('');
        }
    };

    const handleOpenUsageModal = (attributeId: string) => {
        setUsageDetailAttributeId(attributeId);
        setIsUsageModalOpen(true);
    };

    const handleCloseUsageModal = () => {
        setIsUsageModalOpen(false);
        setUsageDetailAttributeId(null);
    };

    const handleOpenAddToSchema = () => {
        setAddToSchemaForm({
            schemaId: '',
            importance_level: 2,
            is_required: false,
            display_order: 0,
            custom_label: '',
            custom_help_text: '',
        });
        setIsAddToSchemaModalOpen(true);
    };

    const handleAddToSchemaConfirm = async () => {
        if (!addToSchemaForm.schemaId || !usageDetailAttributeId) return;

        try {
            await addToSchema.mutateAsync({
                schemaId: addToSchemaForm.schemaId,
                data: {
                    document_attribute_id: usageDetailAttributeId,
                    importance_level: addToSchemaForm.importance_level,
                    is_required: addToSchemaForm.is_required,
                    display_order: addToSchemaForm.display_order,
                    custom_label: addToSchemaForm.custom_label || null,
                    custom_help_text: addToSchemaForm.custom_help_text || null,
                } as SchemaAttributeCreate,
            });
            setIsAddToSchemaModalOpen(false);
            toast.success(t('schemaAttributes.messages.createSuccess', { defaultValue: 'Attributo aggiunto con successo' }));
        } catch {
            toast.error(t('schemaAttributes.messages.saveError', { defaultValue: 'Errore nel salvataggio dell\'attributo' }));
        }
    };

    const handleRemoveFromSchemaConfirm = async () => {
        if (!junctionToRemove) return;

        try {
            await removeFromSchema.mutateAsync({
                schemaId: junctionToRemove.schemaId,
                attrId: junctionToRemove.junctionId,
            });
            setIsRemoveConfirmOpen(false);
            setJunctionToRemove(null);
            toast.success(t('schemaAttributes.messages.deleteSuccess', { defaultValue: 'Attributo rimosso con successo' }));
        } catch {
            toast.error(t('schemaAttributes.messages.deleteError', { defaultValue: 'Errore nella rimozione dell\'attributo' }));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    const categoryOptions = [
        { id: '', label: t('attributes.categoryPlaceholder') },
        ...categories.map((cat) => ({ id: cat!, label: cat! })),
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('attributes.title')}
                    </h2>
                    <p className="text-tertiary mt-1">{t('attributes.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('attributes.newAttribute')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('attributes.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select.ComboBox
                        placeholder={t('attributes.dataTypeSearchPlaceholder', { defaultValue: 'Cerca tipo dato...' })}
                        items={dataTypeOptions}
                        selectedKey={dataTypeFilter}
                        onSelectionChange={(key) => setDataTypeFilter(key ? String(key) : '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select.ComboBox>

                    <Select.ComboBox
                        placeholder={t('attributes.categorySearchPlaceholder', { defaultValue: 'Cerca categoria...' })}
                        items={categoryOptions}
                        selectedKey={categoryFilter}
                        onSelectionChange={(key) => setCategoryFilter(key ? String(key) : '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select.ComboBox>
                </div>
            </div>

            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
                <div className="sticky top-0 z-10 flex items-center justify-between rounded-xl bg-brand-solid p-4 shadow-md">
                    <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-white">
                            {t('attributes.batchDelete.selected', {
                                defaultValue: '{{count}} selezionati',
                                count: selectedIds.size,
                            })}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={handleBatchDeleteStart}
                            size="sm"
                            color="secondary-destructive"
                            iconLeading={Trash01}
                            isDisabled={batchDelete.isPending}
                        >
                            {t('attributes.batchDelete.delete', { defaultValue: 'Elimina' })}
                        </Button>
                        <Button onClick={handleClearSelection} size="sm" color="secondary">
                            {t('attributes.batchDelete.clearSelection', { defaultValue: 'Annulla selezione' })}
                        </Button>
                    </div>
                </div>
            )}

            {/* Attributes Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('attributes.title')} (${sortedAttributes.length})`}
                    description={t('attributes.description')}
                />

                <Table
                    sortDescriptor={sortDescriptor}
                    onSortChange={setSortDescriptor}
                >
                    <Table.Header>
                        <Table.Head id="checkbox" className="w-11 pl-4 md:pl-6">
                            <div
                                role="checkbox"
                                aria-label={t('attributes.selectAll', { defaultValue: 'Select all' })}
                                aria-checked={selectedIds.size === sortedAttributes.length && sortedAttributes.length > 0 ? true : selectedIds.size > 0 ? 'mixed' : false}
                                tabIndex={0}
                                className="cursor-pointer"
                                onClick={() => {
                                    if (selectedIds.size === sortedAttributes.length) {
                                        setSelectedIds(new Set());
                                    } else {
                                        setSelectedIds(new Set(sortedAttributes.map(a => a.document_attribute_id)));
                                    }
                                }}
                            >
                                <CheckboxBase
                                    isSelected={selectedIds.size === sortedAttributes.length && sortedAttributes.length > 0}
                                    isIndeterminate={selectedIds.size > 0 && selectedIds.size < sortedAttributes.length}
                                    size="sm"
                                />
                            </div>
                        </Table.Head>
                        <Table.Head id="label" label={t('attributes.headers.label')} isRowHeader allowsSorting />
                        <Table.Head id="data_type" label={t('attributes.headers.dataType')} allowsSorting />
                        <Table.Head id="category" label={t('attributes.headers.category')} allowsSorting />
                        <Table.Head label={t('attributes.headers.isGlobal')} />
                        <Table.Head id="usage_count" label={t('attributes.headers.usageCount')} allowsSorting />
                        <Table.Head id="created_at" label={t('attributes.headers.createdAt')} allowsSorting />
                        <Table.Head label={t('attributes.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {sortedAttributes.map((attr) => (
                            <Table.Row key={attr.document_attribute_id} id={attr.document_attribute_id}>
                                <Table.Cell>
                                    <div
                                        role="checkbox"
                                        aria-label={attr.label}
                                        aria-checked={selectedIds.has(attr.document_attribute_id)}
                                        tabIndex={0}
                                        className="cursor-pointer"
                                        onClick={() => {
                                            const newSet = new Set(selectedIds);
                                            if (newSet.has(attr.document_attribute_id)) {
                                                newSet.delete(attr.document_attribute_id);
                                            } else {
                                                newSet.add(attr.document_attribute_id);
                                            }
                                            setSelectedIds(newSet);
                                        }}
                                    >
                                        <CheckboxBase
                                            isSelected={selectedIds.has(attr.document_attribute_id)}
                                            size="sm"
                                        />
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{attr.label}</div>
                                    {attr.description && (
                                        <div className="text-sm text-tertiary">{attr.description}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">{t(`attributes.dataTypes.${attr.data_type}`)}</Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{attr.category || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={attr.is_global ? 'success' : 'gray'} size="sm">
                                        {attr.is_global ? t('common.global') : t('common.specific')}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <button
                                        onClick={() => handleOpenUsageModal(attr.document_attribute_id)}
                                        className="text-sm text-brand hover:text-brand-hover underline cursor-pointer"
                                        title={t('attributes.actions.viewUsage', { defaultValue: 'View usage details' })}
                                    >
                                        {attr.usage_count}
                                    </button>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {attr.created_at ? new Date(attr.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(attr)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('attributes.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(attr.document_attribute_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('attributes.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {sortedAttributes.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Batch Delete Modal */}
            <DialogTrigger isOpen={isBatchDeleteModalOpen} onOpenChange={setIsBatchDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-lg rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                {batchDeleteStep === 'confirm' ? (
                                    <>
                                        <h2 className="text-xl font-semibold text-primary mb-4">
                                            {t('attributes.batchDelete.confirmTitle', { defaultValue: 'Conferma Eliminazione' })}
                                        </h2>
                                        <p className="text-tertiary mb-6">
                                            {t('attributes.batchDelete.confirmDescription', {
                                                defaultValue: 'Sei sicuro di voler eliminare {{count}} attributi?',
                                                count: selectedIds.size,
                                            })}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-secondary">
                                                <AlertTriangle className="size-5 text-warning" />
                                            </div>
                                            <h2 className="text-xl font-semibold text-primary">
                                                {t('attributes.batchDelete.warningTitle', { defaultValue: 'Attributi in uso' })}
                                            </h2>
                                        </div>
                                        <p className="text-tertiary mb-4">
                                            {t('attributes.batchDelete.warningDescription', {
                                                defaultValue: 'Alcuni attributi selezionati sono utilizzati in schemi. Eliminandoli verranno rimossi anche dagli schemi associati.',
                                            })}
                                        </p>
                                        <div className="mb-6 max-h-60 overflow-y-auto rounded-lg border border-secondary bg-secondary p-3 space-y-3">
                                            {attributesInUse.map((attr) => (
                                                <div key={attr.document_attribute_id} className="space-y-1">
                                                    <p className="text-sm font-medium text-primary">{attr.attribute_label}</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {attr.schema_names.map((name) => (
                                                            <Badge key={name} color="gray" size="sm">{name}</Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-sm text-tertiary mb-6">
                                            {t('attributes.batchDelete.warningConfirm', {
                                                defaultValue: 'Vuoi procedere comunque con l\'eliminazione di tutti i {{count}} attributi selezionati?',
                                                count: selectedIds.size,
                                            })}
                                        </p>
                                    </>
                                )}
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => {
                                            setIsBatchDeleteModalOpen(false);
                                            setBatchDeleteStep('confirm');
                                            setAttributesInUse([]);
                                        }}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleBatchDeleteConfirm}
                                        size="md"
                                        color="secondary-destructive"
                                        iconLeading={Trash01}
                                        isDisabled={batchDelete.isPending}
                                    >
                                        {batchDelete.isPending
                                            ? t('loading')
                                            : t('attributes.batchDelete.delete', { defaultValue: 'Elimina' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Attribute Edit Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-h-[90vh] overflow-y-auto">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingAttribute ? t('attributes.modal.edit') : t('attributes.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.attributeKey')}
                                            placeholder={t('attributes.modal.fields.attributeKeyPlaceholder')}
                                            value={formData.attribute_key}
                                            onChange={(value) => setFormData({ ...formData, attribute_key: value })}
                                            isRequired
                                            size="md"
                                            isDisabled={!!editingAttribute}
                                        />

                                        <Select.ComboBox
                                            label={t('attributes.modal.fields.dataType')}
                                            items={dataTypeOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.data_type}
                                            onSelectionChange={(key) => handleDataTypeChange(key as AttributeDataType)}
                                            placeholder={t('attributes.modal.fields.dataTypeSearch', { defaultValue: 'Cerca tipo dato...' })}
                                            isRequired
                                            size="md"
                                            isDisabled={!!editingAttribute}
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select.ComboBox>
                                    </div>

                                    <Input
                                        label={t('attributes.modal.fields.label')}
                                        placeholder={t('attributes.modal.fields.labelPlaceholder')}
                                        value={formData.label}
                                        onChange={(value) => setFormData({ ...formData, label: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('attributes.modal.fields.description')}
                                        placeholder={t('attributes.modal.fields.descriptionPlaceholder')}
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    {/* Enum Values - only for string type */}
                                    {formData.data_type === AttributeDataType.STRING && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                {t('attributes.modal.fields.enumValues')}
                                            </label>
                                            <div className="flex gap-2">
                                                <Input
                                                    placeholder={t('attributes.modal.fields.enumValuesPlaceholder')}
                                                    value={enumValueInput}
                                                    onChange={setEnumValueInput}
                                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addEnumValue();
                                                        }
                                                    }}
                                                    size="md"
                                                />
                                                <Button onClick={addEnumValue} size="md" color="secondary">
                                                    <Plus className="size-4" />
                                                </Button>
                                            </div>
                                            {enumValues.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {enumValues.map((val, idx) => (
                                                        <Badge key={idx} color="gray" size="sm" className="flex items-center gap-1">
                                                            {val}
                                                            <button
                                                                onClick={() => removeEnumValue(idx)}
                                                                className="ml-1 hover:text-error"
                                                            >
                                                                <X className="size-3" />
                                                            </button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="text-xs text-tertiary">{t('attributes.modal.fields.enumValuesHelp')}</p>
                                        </div>
                                    )}

                                    {/* Metadata JSON - only for object type */}
                                    {formData.data_type === AttributeDataType.OBJECT && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                {t('attributes.modal.fields.metadata')}
                                            </label>
                                            <textarea
                                                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                                rows={6}
                                                value={metadataJson}
                                                onChange={(e) => {
                                                    setMetadataJson(e.target.value);
                                                    setMetadataError('');
                                                }}
                                                placeholder={t('attributes.modal.fields.metadataPlaceholder')}
                                            />
                                            {metadataError && (
                                                <p className="text-xs text-error">{metadataError}</p>
                                            )}
                                            <p className="text-xs text-tertiary">{t('attributes.modal.fields.metadataHelp')}</p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.inputType')}
                                            placeholder={t('attributes.modal.fields.inputTypePlaceholder')}
                                            value={formData.input_type || ''}
                                            onChange={(value) => setFormData({ ...formData, input_type: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('attributes.modal.fields.placeholder')}
                                            placeholder={t('attributes.modal.fields.placeholderPlaceholder')}
                                            value={formData.placeholder || ''}
                                            onChange={(value) => setFormData({ ...formData, placeholder: value })}
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('attributes.modal.fields.helpText')}
                                        placeholder={t('attributes.modal.fields.helpTextPlaceholder')}
                                        value={formData.help_text || ''}
                                        onChange={(value) => setFormData({ ...formData, help_text: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.category')}
                                            placeholder={t('attributes.modal.fields.categoryPlaceholder')}
                                            value={formData.category || ''}
                                            onChange={(value) => setFormData({ ...formData, category: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('attributes.modal.fields.displayOrder')}
                                            value={String(formData.display_order || 0)}
                                            onChange={(value) => setFormData({ ...formData, display_order: Number(value) || 0 })}
                                            size="md"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingAttribute ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Usage Detail Modal */}
            <DialogTrigger isOpen={isUsageModalOpen} onOpenChange={(open) => {
                setIsUsageModalOpen(open);
                if (!open) setUsageDetailAttributeId(null);
            }}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-2xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-h-[80vh] overflow-y-auto">
                                <h2 className="text-xl font-semibold text-primary mb-2">
                                    {t('attributes.usageModal.title', { defaultValue: 'Utilizzo Attributo' })}
                                </h2>
                                {usageDetail && (
                                    <p className="text-sm text-tertiary mb-4">
                                        {usageDetail.label} ({usageDetail.attribute_key})
                                        {' — '}
                                        {t('attributes.usageModal.usedIn', {
                                            defaultValue: 'Utilizzato in {{count}} schemi',
                                            count: usageDetail.schemas.length,
                                        })}
                                    </p>
                                )}

                                {isLoadingUsage ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
                                    </div>
                                ) : groupedUsage.length === 0 ? (
                                    <p className="text-tertiary py-8 text-center">
                                        {t('attributes.usageModal.noUsage', { defaultValue: 'Non utilizzato in nessuno schema' })}
                                    </p>
                                ) : (
                                    <div className="space-y-4">
                                        {groupedUsage.map(([typeName, schemas]) => (
                                            <div key={typeName}>
                                                <h3 className="text-sm font-semibold text-primary mb-2">{typeName}</h3>
                                                <div className="space-y-2">
                                                    {schemas.map((s) => (
                                                        <div key={s.document_schema_attribute_id}
                                                             className="flex items-center justify-between rounded-lg border border-secondary px-4 py-3">
                                                            <div>
                                                                <span className="text-sm font-medium text-primary">{s.schema_name}</span>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    {getImportanceBadge(s.importance_level)}
                                                                    {s.is_required && <Badge color="error" size="sm">{t('common.required', { defaultValue: 'Required' })}</Badge>}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                onClick={() => {
                                                                    setJunctionToRemove({
                                                                        schemaId: s.document_schema_id,
                                                                        junctionId: s.document_schema_attribute_id,
                                                                        schemaName: s.schema_name,
                                                                    });
                                                                    setIsRemoveConfirmOpen(true);
                                                                }}
                                                                size="sm"
                                                                color="secondary-destructive"
                                                                iconLeading={Trash01}
                                                            >
                                                                {t('attributes.usageModal.remove', { defaultValue: 'Rimuovi' })}
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary">
                                    <Button
                                        onClick={handleOpenAddToSchema}
                                        size="md"
                                        color="primary"
                                        iconLeading={Plus}
                                    >
                                        {t('attributes.usageModal.addToSchema', { defaultValue: 'Aggiungi a Schema' })}
                                    </Button>
                                    <Button
                                        onClick={handleCloseUsageModal}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.close', { defaultValue: 'Chiudi' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Remove from Schema Confirmation */}
            <DialogTrigger isOpen={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('attributes.usageModal.removeConfirmTitle', { defaultValue: 'Conferma Rimozione' })}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('attributes.usageModal.removeConfirmMessage', {
                                        defaultValue: 'Rimuovere questo attributo da "{{schema}}"?',
                                        schema: junctionToRemove?.schemaName || '',
                                    })}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => {
                                            setIsRemoveConfirmOpen(false);
                                            setJunctionToRemove(null);
                                        }}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleRemoveFromSchemaConfirm}
                                        size="md"
                                        color="primary-destructive"
                                        isDisabled={removeFromSchema.isPending}
                                    >
                                        {removeFromSchema.isPending ? t('loading') : t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Add to Schema Modal */}
            <DialogTrigger isOpen={isAddToSchemaModalOpen} onOpenChange={setIsAddToSchemaModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-lg rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {t('attributes.addToSchema.title', { defaultValue: 'Aggiungi Attributo a Schema' })}
                                </h2>

                                <div className="space-y-4">
                                    {/* Attribute (pre-selected, disabled) */}
                                    <Input
                                        label={t('schemaAttributes.modal.fields.attribute', { defaultValue: 'Attributo' })}
                                        value={usageDetail ? `${usageDetail.label} (${usageDetail.attribute_key})` : ''}
                                        isDisabled
                                        size="md"
                                    />

                                    {/* Schema Selector */}
                                    <Select.ComboBox
                                        label={t('attributes.addToSchema.schema', { defaultValue: 'Schema' })}
                                        items={availableSchemas}
                                        selectedKey={addToSchemaForm.schemaId}
                                        onSelectionChange={(key) => setAddToSchemaForm(prev => ({ ...prev, schemaId: key as string }))}
                                        placeholder={t('attributes.addToSchema.schemaPlaceholder', { defaultValue: 'Cerca schema...' })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select.ComboBox>

                                    {/* Importance Level */}
                                    <Select
                                        label={t('schemaAttributes.modal.fields.importanceLevel', { defaultValue: 'Livello Importanza' })}
                                        items={importanceLevelOptions}
                                        selectedKey={String(addToSchemaForm.importance_level)}
                                        onSelectionChange={(key) => setAddToSchemaForm(prev => ({ ...prev, importance_level: Number(key) }))}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    {/* Is Required checkbox */}
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={addToSchemaForm.is_required}
                                            onChange={(e) => setAddToSchemaForm(prev => ({ ...prev, is_required: e.target.checked }))}
                                            className="rounded border-secondary"
                                        />
                                        <label className="text-sm text-primary">
                                            {t('schemaAttributes.modal.fields.isRequired', { defaultValue: 'Required' })}
                                        </label>
                                    </div>

                                    {/* Display Order */}
                                    <Input
                                        label={t('schemaAttributes.modal.fields.displayOrder', { defaultValue: 'Display Order' })}
                                        value={String(addToSchemaForm.display_order)}
                                        onChange={(value) => setAddToSchemaForm(prev => ({ ...prev, display_order: Number(value) || 0 }))}
                                        size="md"
                                    />

                                    {/* Custom Label */}
                                    <Input
                                        label={t('schemaAttributes.modal.fields.customLabel', { defaultValue: 'Custom Label' })}
                                        placeholder={t('schemaAttributes.modal.fields.customLabelPlaceholder', { defaultValue: 'Override label (optional)' })}
                                        value={addToSchemaForm.custom_label}
                                        onChange={(value) => setAddToSchemaForm(prev => ({ ...prev, custom_label: value }))}
                                        size="md"
                                    />

                                    {/* Custom Help Text */}
                                    <Input
                                        label={t('schemaAttributes.modal.fields.customHelpText', { defaultValue: 'Custom Help Text' })}
                                        placeholder={t('schemaAttributes.modal.fields.customHelpTextPlaceholder', { defaultValue: 'Override help text (optional)' })}
                                        value={addToSchemaForm.custom_help_text}
                                        onChange={(value) => setAddToSchemaForm(prev => ({ ...prev, custom_help_text: value }))}
                                        size="md"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={() => setIsAddToSchemaModalOpen(false)} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleAddToSchemaConfirm}
                                        size="md"
                                        color="primary"
                                        isDisabled={!addToSchemaForm.schemaId || addToSchema.isPending}
                                    >
                                        {addToSchema.isPending ? t('loading') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
